import React, { useState } from "react";
import { Box, FormHelperText, Grid, Skeleton, Stack } from "@mui/material";
import fetchStatus from "src/utils/api/fetch-status";
import { WifiTetheringError } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Terms from "src/components/terms";
import { LoadingButton } from "@mui/lab";
import request from "src/utils/request";
import styled from "styled-components";
import WizardIntegrationButton from "src/scenes/setup-wizard/wizard-integration-button";
import githubIcon from "src/assets/icons/github-int";
import slackIconColor from "src/assets/icons/slack-icon-color";
import gitlabIconColor from "src/assets/icons/gitlab-icon-color";
import jiraColorIcon from "src/assets/icons/jira-color-icon";
import googleIconColor from "src/assets/icons/google-icon-color";
import microsoftCalendarIconColor from "src/assets/icons/microsoft-calendar-icon-color";

const Title = styled.p`
    margin: 8px;
`;

const ResponseText = styled(FormHelperText)`
    color: ${({ theme, error }) => (error ? theme.palette.error.main : theme.palette.primary.main)};
`;

const svgs = {
    slack: slackIconColor,
    jira: jiraColorIcon,
    gitlab: gitlabIconColor,
    google: googleIconColor,
    github: githubIcon,
    microsoft: microsoftCalendarIconColor,
};

const IntegrationsSection = ({ integrationsApi }) => {
    const [futureIntegrations] = useState([]);
    const [activeIntegrations] = useState([
        "gitlab",
        "slack",
        "github",
        "microsoft",
        "google",
        "jira",
    ]);

    const [selectedIntegration, setSelectedIntegration] = React.useState();
    const [termsError, setTermsError] = useState(false);
    const [checkedTerms, setCheckedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(false);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (
            integrationsApi.status === fetchStatus.PENDING ||
            integrationsApi.status === fetchStatus.REFETCHING
        ) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [integrationsApi.status]);

    const handleTermsChange = (event) => {
        setCheckedTerms(event.target.checked);
        setTermsError(false);
    };

    const handleIntegration = (clickedIntegration) => () => {
        if (termsError) setTermsError(false);
        const { integrations } = integrationsApi.data;
        const selected = integrations.find(
            (integration) => integration.name === clickedIntegration
        );
        setSelectedIntegration(selected);
    };

    React.useEffect(() => {
        if (integrationsApi.data && selectedIntegration) {
            handleIntegration(selectedIntegration.name)();
        }
    }, [integrationsApi.data]);

    const handleConnect = async () => {
        if (!selectedIntegration.connected) {
            if (!checkedTerms) return setTermsError(true);
            if (selectedIntegration.name === "gitlab") {
                navigate("../integrations/permissions/gitlab", { replace: true });
            }
            if (selectedIntegration.name === "microsoft") {
                navigate("../integrations/permissions/microsoft", { replace: true });
            }
            if (selectedIntegration.name === "slack")
                navigate("../integrations/permissions/slack", { replace: true });
            if (selectedIntegration.name === "github")
                navigate("../integrations/permissions/github", { replace: true });
            if (selectedIntegration.name === "google")
                navigate("../integrations/permissions/google", { replace: true });
            if (selectedIntegration.name === "jira")
                navigate("../integrations/permissions/jira", { replace: true });
        }
        if (selectedIntegration.connected) {
            setLoading(true);
            if (selectedIntegration.name === "gitlab") {
                try {
                    await request("/api/gitlab/integration?type=gitlab", { method: "DELETE" });
                    integrationsApi.refetch();
                    setMessage(
                        "Successfully disconnected gitlab integration. We are sad to see you go.."
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
            if (selectedIntegration.name === "gitlabSelfManaged") {
                try {
                    await request("/api/gitlab/integration?type=gitlabSelfManaged", {
                        method: "DELETE",
                    });
                    integrationsApi.refetch();
                    setMessage(
                        "Successfully disconnected self hosted gitlab integration. We are sad to see you go.."
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
            if (selectedIntegration.name === "slack") {
                try {
                    await request("/api/slack/integration", { method: "DELETE" });
                    integrationsApi.refetch();
                    setMessage(
                        "Successfully disconnected slack integration. We are sad to see you go.."
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
            if (selectedIntegration.name === "github") {
                try {
                    await request("/api/github/integration", { method: "DELETE" });
                    integrationsApi.refetch();
                    setMessage(
                        "Successfully disconnected github integration. We are sad to see you go.."
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
            if (selectedIntegration.name === "google") {
                try {
                    await request("/api/google/integration", { method: "DELETE" });
                    integrationsApi.refetch();
                    setMessage(
                        "Successfully disconnected google integration. We are sad to see you go.."
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
            if (selectedIntegration.name === "jira") {
                try {
                    await request("/api/jira/integration", { method: "DELETE" });
                    integrationsApi.refetch();
                    setMessage(
                        "Successfully disconnected jira integration. We are sad to see you go.."
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            }
        }
        return undefined;
    };

    const renderSkeletons = () => {
        return [0, 1, 2, 3, 4].map((i) => (
            <Grid item key={i}>
                <Skeleton>
                    <WizardIntegrationButton />
                </Skeleton>
            </Grid>
        ));
    };

    const renderError = () => {
        return [0].map((i) => (
            <Grid container item key={i} alignItems="center" direction="column">
                <Grid item>
                    <WifiTetheringError fontSize="large" />
                </Grid>
                <Grid item>Error retrieving data</Grid>
            </Grid>
        ));
    };

    const renderIntegrations = () => {
        if (integrationsApi.status === fetchStatus.PENDING) return renderSkeletons();
        if (integrationsApi.status === fetchStatus.ERROR) return renderError();
        const { integrations } = integrationsApi.data;
        const current = integrations
            .filter(({ name }) => activeIntegrations.includes(name))
            .map(({ name, connected }) => (
                <Grid item>
                    <WizardIntegrationButton
                        svg={svgs[name]}
                        checked={selectedIntegration && selectedIntegration.name === name}
                        connected={connected}
                        action={handleIntegration(name)}
                    />
                </Grid>
            ));

        const futureFilteredIntegrations = futureIntegrations.filter(
            (futureIntegration) => !activeIntegrations.includes(futureIntegration.name)
        );

        const future = futureFilteredIntegrations.map(({ name, connected }) => (
            <Grid item>
                <WizardIntegrationButton
                    svg={svgs[name]}
                    checked={selectedIntegration && selectedIntegration.name === name}
                    connected={connected}
                    action={handleIntegration(name)}
                    future
                />
            </Grid>
        ));
        return (
            <>
                {current}
                {future}
            </>
        );
    };
    return (
        <>
            <Stack direction="row" style={{ width: "100%" }} justifyContent="space-between">
                <Title>Step 1 - Connect with your tools</Title>
                <LoadingButton
                    variant="contained"
                    size="small"
                    color={
                        selectedIntegration && selectedIntegration.connected
                            ? "secondary"
                            : "primary"
                    }
                    onClick={handleConnect}
                    loading={loading}
                    disabled={!selectedIntegration}
                >
                    {selectedIntegration && selectedIntegration.connected
                        ? "Disconnect"
                        : "Connect"}
                </LoadingButton>
            </Stack>
            <Box mt={3} mb={1} mx={4}>
                <Grid container spacing={5} direction="column">
                    <Grid container item spacing={2} justifyContent="space-between">
                        {renderIntegrations()}
                    </Grid>
                    {!selectedIntegration ||
                        (!selectedIntegration.connected && (
                            <Grid
                                item
                                alignItems="center"
                                style={{ display: "flex", paddingTop: 0 }}
                            >
                                <Terms
                                    checkedTerms={checkedTerms}
                                    handleTermsChange={handleTermsChange}
                                    termsError={termsError}
                                />
                            </Grid>
                        ))}
                    {message && (
                        <Grid item>
                            <ResponseText>{message}</ResponseText>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </>
    );
};

export default IntegrationsSection;
